import React, { useEffect, useState } from 'react';
import { DATA_TYPE, FIELD_DETAILS, PROJECT_NAME, TITLE } from '../ISRConstants';
import { TConnector, TField, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput } from '../Pages/DefineModel/model';
import {useISRContext} from "../Hooks/useISRContext";
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { RestrictionsOptions, RestrictionsToggle } from './RestrictionsControl';

type TextFieldProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo?: TIndexState;
}

const FieldComponent = ({id, selectedItem, setUserProjectDataModels, userProjectDataModels, indexInfo}: TextFieldProps) => {
    const [fieldName, setFieldName] = useState<string>('');
    const {fieldUpdate, setFieldUpdate} = useISRContext();

    useEffect(() => {
        if (selectedItem && 'belongsToDataModel' in selectedItem && selectedItem?.belongsToDataModel) {
            setFieldName(userProjectDataModels?.project.dataModels[indexInfo?.dataModelIndex ?? -1]?.data.fields[indexInfo?.fieldIndex ?? -1].name ?? "");
        } else {
            setFieldName(userProjectDataModels?.project.fields[indexInfo?.fieldOutsideDataModelIndex ?? -1]?.data?.name ?? "");
        }
    }, [selectedItem]);

    const updateFieldName = (fieldName: string) => {
        setUserProjectDataModels((prevDataModels) => {
            const newDataModels = { ...prevDataModels };
            if (selectedItem && 'belongsToDataModel' in selectedItem && selectedItem?.belongsToDataModel) {
                const dataModelToUpdate = newDataModels.project.dataModels[indexInfo?.dataModelIndex ?? -1];
                if (dataModelToUpdate && dataModelToUpdate.data && dataModelToUpdate.data.fields[indexInfo?.fieldIndex ?? -1]) {
                    dataModelToUpdate.data.fields[indexInfo?.fieldIndex ?? -1].name = fieldName;
                    const field = dataModelToUpdate.data.fields[indexInfo?.fieldIndex ?? -1];
                    setFieldUpdate(fieldUpdate.filter((e:TField) => e.id !== field.id).concat([dataModelToUpdate.data.fields[indexInfo?.fieldIndex ?? -1]]));
                }
            } else {
                const fieldToUpdate = newDataModels.project.fields[indexInfo?.fieldOutsideDataModelIndex ?? -1];
                if (fieldToUpdate && fieldToUpdate.data) {
                    fieldToUpdate.data.name = fieldName;
                }
            }
        return newDataModels;});
    };

    const handleDataTypeChange = (value: string) => {
        setFieldName(value);
        updateFieldName(value);
    };

    const updateDataConfigField = (patch: Partial<{
        fieldRequired: boolean; recommended: boolean; blockAddToCart: boolean; lockNavigation: boolean;
    }>) => {
        setUserProjectDataModels((prevDataModels) => {
            const newDataModels = { ...prevDataModels };
            if (selectedItem && 'belongsToDataModel' in selectedItem && selectedItem?.belongsToDataModel) {
                const dataModelToUpdate = newDataModels.project.dataModels[indexInfo?.dataModelIndex ?? -1];
                if (dataModelToUpdate?.data?.fields[indexInfo?.fieldIndex ?? -1]) {
                    const config = dataModelToUpdate.data.fields[indexInfo?.fieldIndex ?? -1].dataConfiguration;
                    Object.assign(config, patch);
                }
            } else {
                const fieldToUpdate = newDataModels.project.fields[indexInfo?.fieldOutsideDataModelIndex ?? -1];
                if (fieldToUpdate?.data) {
                    Object.assign(fieldToUpdate.data.dataConfiguration, patch);
                }
            }
            return newDataModels;
        });
    };

    const field = selectedItem as TField;
    const dataConfig = field?.dataConfiguration ?? {};

    const restrictionsEnabled = !!(dataConfig.recommended) || !!(dataConfig.fieldRequired);

    const isRecommended: boolean = dataConfig.recommended !== undefined
        ? !!(dataConfig.recommended)
        : !!(dataConfig.fieldRequired) && !(dataConfig.blockAddToCart) && !(dataConfig.lockNavigation);

    // const isRequired: boolean = !!(dataConfig.fieldRequired);
    const isRequired: boolean = !isRecommended && !!(dataConfig.fieldRequired);
    const isBlockAddToCart = !!(dataConfig.blockAddToCart);
    const isLockNavigation = !!(dataConfig.lockNavigation);

    const handleRestrictionsToggle = (checked: boolean) => {
        if (checked) {
            updateDataConfigField({ recommended: true, fieldRequired: false, blockAddToCart: false, lockNavigation: false });
        } else {
            updateDataConfigField({ recommended: false, fieldRequired: false, blockAddToCart: false, lockNavigation: false });
        }
    };

    const handleSelectRecommended = () => {
        updateDataConfigField({ recommended: true, fieldRequired: false, blockAddToCart: false, lockNavigation: false });
    };

    const handleSelectRequired = () => {
        updateDataConfigField({ recommended: false, fieldRequired: true, blockAddToCart: true, lockNavigation: false });
    };

    const handleBlockAddToCart = (checked: boolean) => {
        if (!checked && !isLockNavigation) {
            toast.warning('Required must have at least one enforcement enabled. Enable Lock Navigation first or disable Required.');
            return;
        }
        updateDataConfigField({ blockAddToCart: checked });
    };

    const handleLockNavigation = (checked: boolean) => {
        if (!checked && !isBlockAddToCart) {
            toast.warning('Required must have at least one enforcement enabled. Enable Block Add to Cart first or disable Required.');
            return;
        }
        updateDataConfigField({ lockNavigation: checked });
    };

    return (
        <Card id={id} data-testid={id} title={FIELD_DETAILS}>
            <div id={`${id}-content`} className='tw-flex tw-items-start tw-gap-3'>
                <TextField
                    id={`${id}-name-input`}
                    data-testid={`${id}-name-input`}
                    label={TITLE}
                    value={fieldName}
                    onChange={handleDataTypeChange}
                    placeholder={PROJECT_NAME}
                    className='tw-flex-1'
                />
                {/*
                    `value`, not `placeholder`. This read-only field showed the data
                    type through the placeholder attribute, so the type rendered in
                    placeholder grey, was not the input's value, and was invisible to
                    anything reading the form programmatically. A disabled field with a
                    real value says the same thing and is actually there.
                */}
                <TextField
                    id={`${id}-type-input`}
                    data-testid={`${id}-type-input`}
                    label={DATA_TYPE}
                    value={selectedItem?.type ?? ''}
                    disabled
                    className='tw-min-w-[200px]'
                />
                <RestrictionsToggle
                    baseId={id}
                    enabled={restrictionsEnabled}
                    onEnabledChange={handleRestrictionsToggle}
                />
            </div>

            <RestrictionsOptions
                baseId={id}
                enabled={restrictionsEnabled}
                mode={isRecommended ? 'recommended' : isRequired ? 'required' : null}
                onSelectRecommended={handleSelectRecommended}
                onSelectRequired={handleSelectRequired}
                blockAddToCart={isBlockAddToCart}
                onBlockAddToCartChange={handleBlockAddToCart}
                lockNavigation={isLockNavigation}
                onLockNavigationChange={handleLockNavigation}
                className='tw-mt-4'
            />
        </Card>
    );
}

export default FieldComponent;